// gpsGate — whether a clock-in may start without a location, and the sentence
// the crew member reads either way.
//
// Two owner instructions, 2026-09-10:
//
//   "Make sure that when clock is started to have gps enabled by default and
//    ask worker to turn gps on if not."
//   "Let's have a setting for admin/manager to toggle if worker is required to
//    have gps on, then that worker can't start the clock if gps isn't on."
//
// ── This deliberately reverses #128, and only when a person says so ─────────
//
// apps/field/geo.js is built around a rule learned from an incident:
// "acquiring a location must NEVER be the reason a shift did not start."
// The second instruction above asks for exactly that, so the reversal is
// isolated here, it is a COMPANY POLICY read from settings, and it is OFF
// unless somebody turned it on. Everything else about geo.js is unchanged: it
// still never rejects, never hangs, and never surfaces a browser error as a
// failed clock-in.
//
// Three things keep the reversal from stranding anybody in a yard:
//
//   1. **Absent means off.** No settings row, an empty object, a phone that
//      is offline and never fetched /api/settings — all read as not required.
//      A crew member must never be blocked by a setting nobody told them about,
//      and "we could not read the policy" is not the same as "the policy says
//      no".
//   2. **The rule is "a location", not "a good one".** A coarse fix satisfies
//      it. Blocking on QUALITY would strand somebody inside a metal barn whose
//      phone can only manage ±2 km — the #128 failure wearing a different hat.
//      A coarse fix gets a warning instead, naming its radius.
//   3. **Every refusal names the fix AND a way out that does not need the
//      phone to co-operate** — call the office. A refusal a person cannot act
//      on is an outage with a polite tone.
//
// The decision is pure, so both screens that clock in (Today, Job) get the
// same answer through actions.js rather than each having their own idea.

import { GPS_STATUS } from './geo';

/** The settings row an admin writes. Server-side it is 'on' | 'off'. */
export const GPS_REQUIRED_KEY = 'gps_required';

/**
 * Past this radius a fix cannot say which property somebody is standing on.
 *
 * 500 m is not a survey figure, it is a usefulness one: a Connecticut
 * residential lot is tens of metres across, so anything wider than a few
 * hundred metres names a neighbourhood rather than an address. A wifi/cell
 * triangulation lands in the 1–3 km range and an iOS fix with Precise Location
 * switched off is deliberately of that order, which is what "a mile away"
 * looks like from the inside.
 */
export const GPS_COARSE_M = 500;

const TRUTHY = new Set(['on', '1', 'true', 'yes']);

/**
 * Does this company require a location before the clock starts?
 *
 * Lenient on read, strict on write: server/routes/settings.js only ever stores
 * 'on' or 'off', but the settings table is TEXT and people put rows in it by
 * hand. A row that plainly means yes must not read as no — a policy silently
 * not applying is worse than one applying too eagerly, because nobody can see
 * it happening.
 */
export function gpsRequired(settings) {
  if (!settings) return false;
  const raw = settings[GPS_REQUIRED_KEY];
  if (raw === true) return true;
  return TRUTHY.has(String(raw === undefined || raw === null ? '' : raw).trim().toLowerCase());
}

/** 1600 -> "1,600". Written out rather than toLocaleString, which varies by machine. */
function grouped(n) {
  const s = String(Math.round(n));
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * "±8 m" / "±1,600 m", or '' when the browser gave no radius.
 *
 * The empty string matters: rendering "±0 m" for a fix whose accuracy the
 * browser withheld would state the opposite of what is known.
 */
export function accuracyText(fix) {
  const a = fix && fix.accuracy;
  if (typeof a !== 'number' || !Number.isFinite(a) || a < 0) return '';
  return `±${grouped(a)} m`;
}

/** A fix too vague to say which property. False when there is no radius to judge. */
export function isCoarse(fix) {
  const a = fix && fix.accuracy;
  if (typeof a !== 'number' || !Number.isFinite(a)) return false;
  return a > GPS_COARSE_M;
}

/**
 * What went wrong and what the person can do about it, per status.
 *
 * Separate advice per cause is the whole point of geo.js keeping the error
 * code: "allow location for this app" is useless to somebody standing in a
 * steel barn, and "step outside" is useless to somebody who denied the prompt.
 */
const ADVICE = {
  [GPS_STATUS.DENIED]: {
    title: 'Location is turned off for this app',
    what: 'Allow location for FieldWorks in your phone settings',
  },
  [GPS_STATUS.UNSUPPORTED]: {
    title: 'This browser cannot report a location',
    what: 'Open FieldWorks from its icon on your home screen',
  },
  [GPS_STATUS.UNAVAILABLE]: {
    title: 'Your phone could not find where it is',
    what: 'Step outside, away from metal and buildings',
  },
  [GPS_STATUS.TIMEOUT]: {
    title: 'Your phone did not find a location in time',
    what: 'Give it a moment somewhere with a clear view of the sky',
  },
  [GPS_STATUS.OFF]: {
    title: 'Record location is switched off',
    what: 'Turn Record location back on under Me',
  },
};

const NOTHING = { allow: true, tone: null, title: null, message: null };

/**
 * @param {object}  args
 * @param {boolean} args.required  the company setting (gpsRequired(settings))
 * @param {string}  args.status    GPS_STATUS.* from geo.locate()
 * @param {object}  args.fix       { lat, lng, accuracy } or null
 * @returns {{allow: boolean, tone: ?string, title: ?string, message: ?string, status: string}}
 */
export function clockInGate({ required = false, status = GPS_STATUS.OK, fix = null } = {}) {
  const carry = (r) => ({ ...r, status });

  if (fix) {
    if (!isCoarse(fix)) return carry(NOTHING);
    // Allowed either way — see (2) in the header. Said out loud either way too,
    // because a fix this wide is the exact thing the owner reported as "a mile
    // away", and the person holding the phone is the only one who can move.
    return carry({
      allow: true,
      tone: 'warn',
      title: 'Location is rough',
      message: `Recorded to ${accuracyText(fix)} — that is a neighbourhood, not an address. Turn on precise location for FieldWorks in your phone settings so it shows where you actually are.`,
    });
  }

  const advice = ADVICE[status] || ADVICE[GPS_STATUS.UNAVAILABLE];

  if (!required) {
    // The person's own switch, deliberately silent: they set it, and nagging
    // somebody about a setting they chose is how a notice becomes something
    // everybody taps past without reading.
    //
    // OK-with-no-fix is a state geo.locate() cannot produce; it is what a bare
    // clockInGate() defaults to. There is nothing true to say about it, so it
    // says nothing rather than guessing at advice.
    if (status === GPS_STATUS.OFF || status === GPS_STATUS.OK) return carry(NOTHING);
    return carry({
      allow: true,
      tone: 'warn',
      title: advice.title,
      message: `${advice.what} if you want your clock-ins stamped with where you were.`,
    });
  }

  return carry({
    allow: false,
    tone: 'stop',
    title: advice.title,
    message: `Your company records where each shift starts, so the clock cannot start without it. ${advice.what}, then tap Clock in again. If it keeps failing, call the office — they can start the clock for you.`,
  });
}
